import {
    CommandResponse,
    EntityCommandCreateResponse,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    ScheduleOfActivitiesSection,
    StudyEntityType,
    StudySection,
    toScheduleOfActivitiesSection,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { StudyPathParameter } from '../parameter';

const factory = createStudyEntityHookFactory<ScheduleOfActivitiesSection>(StudyEntityType.ScheduleOfActivitiesSection);

export const useScheduleOfActivitiesSections = factory.list(
    (
        { studyId, ...query }: StudyPathParameter & { revision?: number },
        signal?: AbortSignal
    ): Promise<ScheduleOfActivitiesSection[]> => {
        return axios
            .get(`/api/study-designer/studies/${studyId}/schedule-of-activities-section`, {
                signal,
                params: query,
            })
            .then(response => (response.data ?? []).map(toScheduleOfActivitiesSection));
    }
);

export const useScheduleOfActivitiesSectionInsert = factory.mutate(
    (entity: Partial<ScheduleOfActivitiesSection>): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> => {
        const { studyId, ...section } = entity;
        return axios
            .post(`/api/study-designer/studies/${studyId}/schedule-of-activities-section`, section)
            .then(response => response.data);
    }
);

export const useScheduleOfActivitiesSectionUpdate = factory.mutate(
    (entity: ScheduleOfActivitiesSection): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/schedule-of-activities-section/${entity.id}`, entity)
            .then(response => response.data);
    },
    {
        optimisticUpdate: (scheduleOfActivitiesSection, optimisticData) => {
            return optimisticData.map(x => (x.id === scheduleOfActivitiesSection.id ? scheduleOfActivitiesSection : x));
        },
    }
);

export const useScheduleOfActivitiesSectionDelete = factory.mutate(
    (entity: { id: string; studyId: string }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/schedule-of-activities-section/${entity.id}`)
            .then(response => response.data);
    }
);

export const useScheduleOfActivitiesSectionDuplicate = factory.mutate(
    (entity: { id: string; studyId: string }): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> => {
        return axios
            .post(
                `/api/study-designer/studies/${entity.studyId}/schedule-of-activities-section/${entity.id}/actions/clone`,
                {}
            )
            .then(response => response.data);
    }
);

export const useScheduleOfActivitiesSectionMove = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        placement: PlacementPayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, placement } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/schedule-of-activities-section/${id}/actions/move`, placement)
            .then(response => response.data);
    }
);

export const useScheduleOfActivitiesSectionLink = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, linkedSection } = entity;
        return axios
            .put(
                `/api/study-designer/studies/${studyId}/schedule-of-activities-section/${id}/actions/link`,
                linkedSection
            )
            .then(response => response.data);
    }
);

export const useScheduleOfActivitiesSectionRename = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        renamePayload: RenamePayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, renamePayload } = entity;
        return axios
            .put(
                `/api/study-designer/studies/${studyId}/schedule-of-activities-section/${id}/actions/rename`,
                renamePayload
            )
            .then(response => response.data);
    }
);
